import { useCallback, useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { CASES, OldSite, NewSite } from "./BeforeAfterViews";
import { Reveal, SectionLabel } from "./Reveal";
import { useUI } from "@/context/UIContext";
import { useWidth } from "./minisites/shared";

function Compare({ c }) {
  const [pos, setPos] = useState(50);
  const box = useRef(null);
  const anim = useRef(null);
  const touched = useRef(false);
  const inView = useInView(box, { once: true, amount: 0.4 });
  const [wref, w] = useWidth();
  const narrow = w > 0 && w < 640;
  const base = narrow ? 400 : 1200;
  const H = narrow ? 720 : 740;
  const scale = w ? w / base : 0;

  const stopDemo = useCallback(() => { touched.current = true; anim.current?.stop(); }, []);

  useEffect(() => {
    if (!inView || touched.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    anim.current = animate(50, [50, 80, 22, 50], {
      duration: 2.6, ease: "easeInOut", times: [0, 0.35, 0.75, 1], delay: 0.3,
      onUpdate: (v) => { if (!touched.current) setPos(v); },
    });
    return () => anim.current?.stop();
  }, [inView]);

  const fromEvent = (e) => {
    const r = box.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)));
  };
  const onDown = (e) => { stopDemo(); box.current.setPointerCapture?.(e.pointerId); fromEvent(e); };
  const onMove = (e) => { if (box.current.hasPointerCapture?.(e.pointerId)) fromEvent(e); };
  const onKey = (e) => {
    const map = { ArrowLeft: -5, ArrowRight: 5, PageDown: -20, PageUp: 20 };
    if (map[e.key] !== undefined) { e.preventDefault(); stopDemo(); setPos((p) => Math.max(0, Math.min(100, p + map[e.key]))); }
    if (e.key === "Home") { e.preventDefault(); stopDemo(); setPos(0); }
    if (e.key === "End") { e.preventDefault(); stopDemo(); setPos(100); }
  };
  const jump = (v) => { stopDemo(); setPos(v); };

  return (
    <div ref={wref}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <button type="button" data-testid="compare-before-btn" onClick={() => jump(100)} className={`rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${pos > 95 ? "border-ink bg-ink text-paper" : "border-line bg-white"}`}>Vorher</button>
        <span className="eyebrow hidden text-[11px] text-mute sm:block">Regler ziehen oder Pfeiltasten nutzen</span>
        <button type="button" data-testid="compare-after-btn" onClick={() => jump(0)} className={`rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${pos < 5 ? "border-gold bg-gold text-ink" : "border-line bg-white"}`}>Nachher</button>
      </div>
      <div ref={box} id="compare-stage" data-testid="compare-stage" onPointerDown={onDown} onPointerMove={onMove}
        className="relative select-none overflow-hidden rounded-2xl border border-line bg-white shadow-[0_30px_60px_-40px_rgba(26,28,30,.5)]"
        style={{ height: H * scale || 400, touchAction: "pan-y", cursor: "ew-resize" }}>
        {scale > 0 && (
          <>
            <div className="absolute left-0 top-0" style={{ width: base, height: H, transform: `scale(${scale})`, transformOrigin: "0 0" }}><NewSite c={c} narrow={narrow} /></div>
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
              <div className="absolute left-0 top-0" style={{ width: base, height: H, transform: `scale(${scale})`, transformOrigin: "0 0" }}><OldSite c={c} narrow={narrow} /></div>
            </div>
          </>
        )}
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-ink/85 px-3 py-1 text-[12px] font-semibold text-white" style={{ opacity: pos > 12 ? 1 : 0 }}>Vorher</span>
        <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-gold px-3 py-1 text-[12px] font-semibold text-ink" style={{ opacity: pos < 88 ? 1 : 0 }}>Nachher</span>
        <div className="pointer-events-none absolute inset-y-0 w-[2px] -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,.15)]" style={{ left: `${pos}%` }} />
        <div role="slider" tabIndex={0} aria-label="Vergleichsregler Vorher und Nachher" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pos)}
          aria-valuetext={`${Math.round(pos)} Prozent Vorher-Ansicht`} onKeyDown={onKey} data-testid="compare-handle"
          className="absolute top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-ink text-white shadow-xl transition-transform active:scale-95"
          style={{ left: `${pos}%` }}>
          <ChevronsLeftRight className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [k, setK] = useState(0);
  const { openDemo } = useUI();
  return (
    <section id="vorher-nachher" className="py-20 sm:py-28" data-testid="before-after-section">
      <div className="wrap">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <Reveal>
            <SectionLabel n="01">Vorher / Nachher</SectionLabel>
            <h2 className="mt-5 max-w-[760px] font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
              Sehen Sie, was ein neuer Auftritt <span className="font-serif font-normal italic">verändert.</span>
            </h2>
            <p className="mt-4 max-w-[560px] text-base text-mute sm:text-lg">Dasselbe Unternehmen, dieselben Inhalte, dieselben Bilder – einmal veraltet, einmal neu gestaltet.</p>
          </Reveal>
          <div role="tablist" aria-label="Branche wählen" className="no-scrollbar flex gap-2 overflow-x-auto">
            {CASES.map((c, i) => (
              <button key={c.key} role="tab" type="button" aria-selected={k === i} data-testid={`compare-tab-${c.key}`} onClick={() => setK(i)}
                className={`shrink-0 rounded-full px-5 py-3 text-[14px] font-semibold transition-colors ${k === i ? "bg-ink text-paper" : "border border-line bg-white text-ink hover:border-ink"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="mt-10">
          <Compare key={CASES[k].key} c={CASES[k]} />
          <div className="mt-4 flex flex-col justify-between gap-3 text-[13px] text-mute sm:flex-row sm:items-center">
            <span className="flex items-center gap-2" data-testid="compare-study-label"><span className="rounded-full border border-gold/60 bg-gold-soft px-2.5 py-0.5 font-semibold text-gold-deep">Illustrative Designstudie</span> Fiktives Unternehmen „{CASES[k].name}“ – kein Kundenprojekt.</span>
            <button type="button" data-testid="compare-demo-cta" onClick={() => openDemo()} className="w-fit font-semibold text-ink underline decoration-gold decoration-2 underline-offset-4">So eine Verwandlung für mein Unternehmen →</button>
          </div>
        </div>
      </div>
    </section>
  );
}
